import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

const FAQ = () => {
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);
  const [headerRef, headerVisible] = useIntersectionObserver();
  const [accordionRef, accordionVisible] = useIntersectionObserver({ rootMargin: "50px" });
  
  const faqs = [
    {
      question: "How do I book my slot?",
      answer: "Simply click on 'Reserve Your Spot' button for your preferred plan, fill out the booking form with your details and arrival date, and our team will contact you within 24 hours to confirm and finalize the arrangements."
    },
    {
      question: "Can my parents track updates?",
      answer: "Absolutely! We provide regular updates via WhatsApp and email to both students and their families. For Premium Anchor plan, we also offer weekly video updates specifically for families back home, ensuring complete transparency and peace of mind."
    },
    {
      question: "What happens if my flight is delayed?",
      answer: "No worries! We monitor all flight statuses in real-time. If there's a delay, our team automatically adjusts the pickup schedule and keeps you and your family informed. Your safety and convenience are our top priorities."
    },
    {
      question: "Is the booking fee refundable?",
      answer: "Yes, we offer a full refund if you cancel at least 7 days before your arrival date. For cancellations within 7 days, a 50% refund is provided. We understand plans can change, and we try to be as flexible as possible."
    },
    {
      question: "Which cities do you operate in?",
      answer: "Currently, we offer our comprehensive services in Melbourne. As we're based in Melbourne and both founders studied here, we know the city inside out. We're planning to expand to other cities soon based on demand!"
    },
    {
      question: "What makes Ace Anchor different from other services?",
      answer: "We're founded by two former international students who understand exactly what you're going through. We don't just provide services - we provide empathy, understanding, and genuine care. Plus, our unique 'Home to Horizon' service ensures support from your doorstep to your new home in Melbourne."
    }
  ];

  const displayedFaqs = isMobile && !showAll ? faqs.slice(0, 4) : faqs;

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <header 
            ref={headerRef}
            className={`text-center mb-8 md:mb-12 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
              Got Questions?{" "}
              <span className="text-secondary">We've Got You.</span>
            </h2>
            <p className="text-base md:text-xl text-muted-foreground">
              <span className="hidden md:inline">Find answers to common questions about our services.</span>
              <span className="md:hidden">Common questions answered</span>
            </p>
          </header>

          <div
            ref={accordionRef}
            className={`transition-all duration-700 ${
              accordionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <Accordion type="single" collapsible className="space-y-3 md:space-y-4">
              {displayedFaqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card rounded-xl px-4 md:px-6 border-0 shadow-soft hover:shadow-card transition-shadow duration-300"
                >
                  <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-foreground hover:no-underline py-4 md:py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-muted-foreground pb-4 md:pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {isMobile && faqs.length > 4 && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => setShowAll(!showAll)}
                  variant="outline"
                  size="sm"
                >
                  {showAll ? "Show Less" : `Show ${faqs.length - 4} More Questions`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
